
import React, { useState, useEffect } from 'react';
import { BookModel, MemberModel, ScheduleEvent, SiteSettingModel, ScheduleMarker, SUBJECT_TYPES, GRADE_TYPES } from '../../../../models';
import { db } from '../../../../firebase';
import { collection, query, getDocs } from 'firebase/firestore';
import { Search, Book } from 'lucide-react';

interface ScheduleEditManagementProps {
    member: MemberModel;
    siteSettings: SiteSettingModel;
    scheduleMarkers: ScheduleMarker[];
    initialStartDate?: string;
    initialEndDate?: string;
    onSave: (events: ScheduleEvent[]) => void;
    onSaveMarker?: (marker: ScheduleMarker) => void;
    onCancel: () => void;
}

type CreateTab = 'book' | 'event';

const ScheduleEditManagement: React.FC<ScheduleEditManagementProps> = ({
    member, siteSettings, scheduleMarkers,
    initialStartDate, initialEndDate,
    onSave, onSaveMarker, onCancel
}) => {
    // Mode
    const [createTab, setCreateTab] = useState<CreateTab>('book');
    const [step, setStep] = useState<1 | 2>(1);

    // Book Search State
    const [searchTerm, setSearchTerm] = useState('');
    const [books, setBooks] = useState<BookModel[]>([]);
    const [selectedBook, setSelectedBook] = useState<BookModel | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedGrade, setSelectedGrade] = useState(member.grade || '');

    // Schedule Generation Settings
    const [unitType, setUnitType] = useState<string>('');
    const [startIndex, setStartIndex] = useState(1);
    const [dailyLoad, setDailyLoad] = useState(1);
    const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]);

    // Creation now strictly relies on props

    // Marker Creation State
    const [markerTitle, setMarkerTitle] = useState('');
    const [markerType, setMarkerType] = useState<'vacation' | 'exam' | 'makeup' | 'etc'>('vacation');
    const [markerMemo, setMarkerMemo] = useState('');

    // Fetch Books
    const handleSearch = async () => {
        setIsLoading(true);
        try {
            let q = query(collection(db, 'books'));
            const snap = await getDocs(q);
            let list = snap.docs.map(d => ({ id: d.id, ...d.data() } as BookModel));

            if (searchTerm) list = list.filter(b => b.title.toLowerCase().includes(searchTerm.toLowerCase()));
            if (selectedSubject) list = list.filter(b => b.subject === selectedSubject);
            if (selectedGrade) list = list.filter(b => b.grade === selectedGrade);

            setBooks(list.slice(0, 20));
        } catch (error) {
            console.error("Search error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Auto-search on mount & Init Unit Type
    useEffect(() => {
        if (step === 1) handleSearch();
    }, [step, selectedSubject, selectedGrade]);

    const bookUnits = [
        { unit: '페이지', count: 500 },
        { unit: '단원', count: 30 }
    ];

    useEffect(() => {
        if (selectedBook) {
            setUnitType('페이지');
        } else {
            setUnitType('');
        }
    }, [selectedBook]);

    const getSubjectColor = (subject: string) => {
        const colors: Record<string, string> = {
            '국어': 'bg-rose-500', '수학': 'bg-blue-600', '영어': 'bg-indigo-600',
            '한국사': 'bg-amber-700', '사회': 'bg-orange-600', '과학': 'bg-emerald-600',
        };
        return colors[subject] || 'bg-slate-600';
    };

    const handleGenerate = () => {
        if (!selectedBook) return;

        let maxVal = 0;
        const targetUnit = bookUnits.find(u => u.unit === unitType);
        if (targetUnit) maxVal = targetUnit.count;

        if (startIndex > maxVal) return alert('시작 번호가 전체 분량을 초과합니다.');
        if (dailyLoad <= 0) return alert('하루 학습량을 1 이상으로 입력하세요.');
        if (selectedDays.length === 0) return alert('학습 요일을 선택하세요.');
        if (!initialStartDate) return alert('시작일이 설정되지 않았습니다.');

        const newEvents: ScheduleEvent[] = [];
        const [y, m, d] = initialStartDate.split('-').map(Number);
        let currentVal = startIndex;
        let dateCursor = new Date(y, m - 1, d);
        let dayIndex = 0;

        let truncated = false;
        const endDateObj = initialEndDate ? new Date(initialEndDate) : null;

        while (currentVal <= maxVal) {
            // Check end date constraint
            if (endDateObj) {
                const cursorStr = dateCursor.toISOString().split('T')[0];
                const endStr = endDateObj.toISOString().split('T')[0];
                if (cursorStr > endStr) {
                    truncated = true;
                    break;
                }
            }

            // Check Special Events (Vacation/Exam) -> Skip
            const cursorStr = dateCursor.toISOString().split('T')[0];
            const isHoliday = scheduleMarkers.some(m =>
                (m.type === 'vacation' || m.type === 'exam') &&
                cursorStr >= m.startDate && cursorStr <= m.endDate
            );

            if (isHoliday) {
                dateCursor.setDate(dateCursor.getDate() + 1);
                continue;
            }

            // Skip non-selected days
            if (!selectedDays.includes(dateCursor.getDay())) {
                dateCursor.setDate(dateCursor.getDate() + 1);
                continue;
            }

            const unitStart = currentVal;
            const unitEnd = Math.min(currentVal + dailyLoad - 1, maxVal);

            // Dynamic Label Generation
            let taskText = `${unitType} ${unitStart} ~ ${unitEnd}`;
            if (unitType === '단원' || unitType === 'chapters') taskText = `단원 ${unitStart} ~ ${unitEnd}`;
            else if (unitType === '쪽' || unitType === '페이지' || unitType === 'pages') taskText = `쪽 ${unitStart} ~ ${unitEnd}`;
            else if (unitType === '문항' || unitType === 'questions') taskText = `문항 ${unitStart} ~ ${unitEnd}`;
            else if (unitType === '일수' || unitType === 'days') taskText = `Day ${unitStart}`;

            // If unitStart == unitEnd, simplify
            if (unitStart === unitEnd) {
                if (unitType === '단원' || unitType === 'chapters') taskText = `단원 ${unitStart}`;
                else if (unitType === '쪽' || unitType === '페이지' || unitType === 'pages') taskText = `쪽 ${unitStart}`;
                else if (unitType === '문항' || unitType === 'questions') taskText = `문항 ${unitStart}`;
                else if (unitType === '일수' || unitType === 'days') taskText = `Day ${unitStart}`;
                else taskText = `${unitType} ${unitStart}`;
            }

            newEvents.push({
                id: '',
                bookId: selectedBook.id,
                bookTitle: selectedBook.title,
                subject: selectedBook.subject,
                memberId: member.id,
                memberName: member.name,
                date: dateCursor.getTime(),
                task: taskText,
                unitType,
                startVal: unitStart,
                endVal: unitEnd,
                taskIndex: dayIndex,
                status: 'pending'
            });

            currentVal = unitEnd + 1;
            dateCursor.setDate(dateCursor.getDate() + 1);
            dayIndex++;
        }

        if (newEvents.length === 0) {
            if (truncated) return alert('선택한 기간 내에 생성할 수 있는 일정이 없습니다.');
            else return alert('생성할 일정이 없습니다.');
        }

        onSave(newEvents);

        if (truncated) {
            alert(`${newEvents.length}개의 일정이 생성되었습니다.\n(설정된 기간을 초과하여 일부 일정은 생성되지 않았습니다.)`);
        } else {
            alert(`${newEvents.length}개의 일정이 생성되었습니다.`);
        }

        onCancel(); // Done, go back
    };

    const handleCreateMarker = () => {
        if (!markerTitle) return alert('일정 제목을 입력하세요.');
        if (!initialStartDate || !initialEndDate) return alert('기간을 설정하세요.');

        onSaveMarker?.({
            id: '',
            memberId: member.id,
            type: markerType,
            startDate: initialStartDate,
            endDate: initialEndDate,
            title: markerTitle,
            memo: markerMemo
        });

        // Reset and maybe notify? 
        alert('특별 일정이 추가되었습니다.');
        setMarkerTitle('');
        setMarkerMemo('');
        onCancel();
    };

    return (
        <div className="flex-1 overflow-hidden flex flex-col sm:flex-row bg-slate-50">
            {step === 1 ? (
                <div className="flex-1 flex flex-col w-full">
                    {/* Header */}
                    <div className="p-4 bg-white border-b border-slate-100">
                        <div className="flex items-center gap-2 mb-3 p-1 bg-slate-100 rounded-lg self-start w-fit">
                            <button
                                onClick={() => setCreateTab('book')}
                                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${createTab === 'book' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                교재 학습
                            </button>
                            <button
                                onClick={() => setCreateTab('event')}
                                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${createTab === 'event' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                특별 일정
                            </button>
                        </div>

                        {createTab === 'book' ? (
                            <div className="flex gap-3 w-full">
                                <select value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)} className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold outline-none">
                                    <option value="">전체 과목</option>
                                    {(siteSettings.subjects && siteSettings.subjects.length > 0 ? siteSettings.subjects : SUBJECT_TYPES).map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <select value={selectedGrade} onChange={e => setSelectedGrade(e.target.value)} className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold outline-none">
                                    <option value="">전체 대상</option>
                                    {(siteSettings.bookGrades && siteSettings.bookGrades.length > 0 ? siteSettings.bookGrades : (siteSettings.grades && siteSettings.grades.length > 0 ? siteSettings.grades : GRADE_TYPES)).map(g => <option key={g} value={g}>{g}</option>)}
                                </select>
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                                    <input
                                        type="text" placeholder="교재 제목 검색..." value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                        className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="text-xs font-bold text-slate-400">
                                방학, 시험기간, 보강 등 특별한 일정을 캘린더에 표시합니다.
                            </div>
                        )}
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                        {createTab === 'book' ? (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                {books.map(book => (
                                    <button key={book.id} onClick={() => { setSelectedBook(book); setStep(2); }} className="text-left group">
                                        <div className="aspect-[3/4] bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden relative mb-2 group-hover:border-indigo-500 transition-all">
                                            {book.coverUrl ? <img src={book.coverUrl} className="w-full h-full object-cover" /> : <div className={`w-full h-full flex items-center justify-center text-white ${getSubjectColor(book.subject)}`}><Book size={24} /></div>}
                                        </div>
                                        <p className="text-xs font-black text-slate-800 line-clamp-1">{book.title}</p>
                                        <p className="text-[10px] text-slate-400">{book.publisher}</p>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="max-w-md mx-auto space-y-4 pt-4">
                                <div>
                                    <label className="text-xs font-black text-slate-400 mb-1 block">일정 제목</label>
                                    <input value={markerTitle} onChange={e => setMarkerTitle(e.target.value)} className="w-full p-3 bg-white border border-slate-200 rounded-xl font-bold text-sm outline-none focus:border-indigo-500" placeholder="예: 여름방학, 기말고사 기간" />
                                </div>
                                <div>
                                    <label className="text-xs font-black text-slate-400 mb-1 block">일정 유형</label>
                                    <div className="flex bg-slate-100 p-1 rounded-xl">
                                        {(['vacation', 'exam', 'makeup', 'etc'] as const).map(t => (
                                            <button key={t} onClick={() => setMarkerType(t)} className={`flex-1 py-2 rounded-lg text-xs font-bold ${markerType === t ? 'bg-white shadow text-indigo-600' : 'text-slate-400'}`}>
                                                {t === 'vacation' ? '방학' : t === 'exam' ? '시험' : t === 'makeup' ? '보강' : '기타'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                                    <h4 className="text-xs font-black text-slate-400 mb-2">선택된 기간 (자동 적용)</h4>
                                    <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                                        <span>{initialStartDate || '-'}</span>
                                        <span>~</span>
                                        <span>{initialEndDate || '-'}</span>
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-1">
                                        * 기간 변경은 캘린더에서 다시 선택해주세요.
                                    </p>
                                </div>
                                <div>
                                    <label className="text-xs font-black text-slate-400 mb-1 block">메모 (선택)</label>
                                    <textarea value={markerMemo} onChange={e => setMarkerMemo(e.target.value)} className="w-full p-3 bg-white border border-slate-200 rounded-xl font-bold text-sm outline-none focus:border-indigo-500 h-20 resize-none" placeholder="메모를 입력하세요..." />
                                </div>
                                <button onClick={handleCreateMarker} className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all">
                                    일정 등록하기
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex-1 flex flex-col sm:flex-row overflow-hidden w-full">
                    {/* Simple Config Selection for conciseness */}
                    <div className="flex-1 p-6 overflow-y-auto custom-scrollbar space-y-6">
                        <div className="flex flex-col gap-4 p-4 bg-white border border-slate-100 rounded-2xl shadow-sm">
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-20 bg-slate-100 rounded-lg overflow-hidden shrink-0">
                                    {selectedBook?.coverUrl ? <img src={selectedBook.coverUrl} className="w-full h-full object-cover" /> : <div className={`w-full h-full ${getSubjectColor(selectedBook?.subject || '')}`} />}
                                </div>
                                <div>
                                    <h4 className="text-sm font-black">{selectedBook?.title}</h4>
                                    <div className="flex gap-2 mt-1">
                                        <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded text-slate-500">{selectedBook?.subject}</span>
                                        <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded text-slate-500">{selectedBook?.grade}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 border-t border-slate-50 pt-3">
                                {bookUnits.map((u, idx) => (
                                    <div key={idx} className="text-center border-l border-slate-50 first:border-l-0">
                                        <p className="text-[10px] text-slate-400 font-medium">{u.unit}</p>
                                        <p className="text-xs font-black text-slate-700">{u.count}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-black text-slate-400 mb-1 block">학습 단위</label>
                                <div className="flex bg-slate-200 p-1 rounded-xl gap-1 overflow-x-auto no-scrollbar">
                                    {bookUnits.map(u => (
                                        <button key={u.unit} onClick={() => setUnitType(u.unit)}
                                            className={`flex-1 py-2 rounded-lg text-xs font-bold whitespace-nowrap px-3 ${unitType === u.unit ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>
                                            {u.unit}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-black text-slate-400 mb-1 block">시작 번호</label>
                                    <input type="number" value={startIndex} onChange={e => setStartIndex(Number(e.target.value))} className="w-full bg-white border border-slate-200 p-3 rounded-xl font-bold text-sm outline-none focus:border-indigo-500" />
                                </div>
                                <div>
                                    <label className="text-xs font-black text-slate-400 mb-1 block">하루 분량</label>
                                    <input type="number" value={dailyLoad} onChange={e => setDailyLoad(Number(e.target.value))} className="w-full bg-white border border-slate-200 p-3 rounded-xl font-bold text-sm outline-none focus:border-indigo-500" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-black text-slate-400 mb-1 block">학습 요일</label>
                                <div className="flex gap-2">
                                    {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
                                        <button key={i} onClick={() => setSelectedDays(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i])}
                                            className={`w-8 h-8 rounded-lg text-xs font-bold border ${selectedDays.includes(i) ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-slate-400'}`}>
                                            {d}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-black text-slate-400 mb-1 block">적용 기간 (캘린더 선택)</label>
                                <div className="w-full bg-slate-100 border border-slate-200 p-3 rounded-xl font-bold text-sm text-slate-600 flex items-center justify-between">
                                    <span>{initialStartDate} ~ {initialEndDate || '설정되지 않음'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 flex gap-3">
                            <button onClick={() => setStep(1)} className="flex-1 py-3 bg-slate-200 text-slate-600 rounded-xl font-bold text-xs">이전</button>
                            <button onClick={handleGenerate} className="flex-[2] py-3 bg-indigo-600 text-white rounded-xl font-bold text-xs shadow-lg shadow-indigo-200 hover:bg-indigo-700">일정 생성하기</button>
                        </div>
                    </div>
                </div>
            )}
            <div className="p-4 border-t border-slate-100 bg-white sm:hidden">
                <button onClick={onCancel} className="w-full py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-xs">취소하고 목록으로</button>
            </div>
        </div>
    );
};

export default ScheduleEditManagement;
